/**
 * Transient click-feedback markers — the instant, client-side acknowledgment
 * that replaced the persistent red-X (interact) / yellow-X (walk) waypoints
 * (RENDERING.md §Click markers). A marker is an input ack, never a success
 * ack: it spawns in the same frame as the pointer-down, classifies the click
 * as ground (walk) / interact (entity) / invalid (no-op), and fades out in
 * ≤400 ms. Purely visual — never part of game state, save data, or network
 * traffic.
 *
 * Adapted from the prototype in click-feedback-scratch.txt (16-sample ring
 * silhouette bake, path/sprite outline modes, dark halo on every stroke) to
 * this game: TypeScript, the iso painter pass (markers are painter-sorted
 * drawables, so walls occlude them like anything else), static interactables
 * (no moving targets), no audio (deferred), and a fixed 2:1 dimetric floor
 * (ground ellipse ratio 2.0, not the prototype's 2.2).
 *
 * Pure classification/geometry helpers are at the top (unit-testable in node,
 * mirroring actions.ts / iso.ts); the DOM outline helpers (silhouette bake +
 * cache) live below and are exercised through the renderer.
 */
import type { PrimaryAction, WorldObject } from "./actions.js";

/** All durations in ms; all sizes in css px at unit scale u (the renderer
 *  multiplies by u so markers scale with zoom). */
export interface MarkerConfig {
  interactColor: string;
  neutralColor: string;
  invalidColor: string;
  haloColor: string;
  haloAlpha: number;
  /** Extra dark-halo width beyond the marker's own stroke (×u px). */
  haloWidthBonus: number;

  groundDuration: number;
  groundStartRx: number;
  groundEndRx: number;
  /** Ellipse width:height — 2.0 on this game's 2:1 dimetric floor (a ground
   *  circle projects to a 2:1 ellipse here; the prototype's 2.2 assumed a
   *  different camera angle). */
  groundEllipseRatio: number;
  groundStroke: number;

  interactDuration: number;
  /** Fraction of the duration the outline holds full opacity before fading. */
  interactHoldFrac: number;
  interactDilation: number;
  /** Optional 30%-alpha fill of the target silhouette for the first
   *  bodyTintDuration ms. Off by default — on small item sprites it washes
   *  them out; tunable for the door slab later if wanted. */
  bodyTintAlpha: number;
  bodyTintDuration: number;

  invalidDuration: number;
  invalidRadius: number;
  invalidShakePx: number;
  invalidStroke: number;
}

export const MARKER_CONFIG: MarkerConfig = {
  interactColor: "#3FD6E0",
  neutralColor: "#FFFFFF",
  invalidColor: "#9A9A94",
  haloColor: "#101010",
  haloAlpha: 0.9,
  haloWidthBonus: 2.5,

  groundDuration: 380,
  groundStartRx: 5,
  groundEndRx: 35,
  groundEllipseRatio: 2.0,
  groundStroke: 2,

  interactDuration: 350,
  interactHoldFrac: 0.35,
  interactDilation: 2,
  bodyTintAlpha: 0,
  bodyTintDuration: 120,

  invalidDuration: 250,
  invalidRadius: 9,
  invalidShakePx: 4,
  invalidStroke: 2,
};

/** A short screen-space receipt at the actual pointer position. World markers
 *  still show the resolved destination/target; this only says which kind of
 *  command the click selected. It never indicates server success. */
export type CursorMark = { kind: "walk" | "interact" | "invalid"; x: number; y: number; born: number };
export const CURSOR_MARK_DURATION = 320;

export function cursorMarkForAction(action: PrimaryAction, x: number, y: number, born: number): CursorMark {
  switch (action.kind) {
    case "walk":
      return { kind: "walk", x, y, born };
    case "interact":
    case "enter":
    case "openSack":
    case "attack":
      return { kind: "interact", x, y, born };
    case "self":
    case "none":
      return { kind: "invalid", x, y, born };
  }
}

/** Fixed-size screen-space mark, above the world and host overlays. A yellow
 *  diamond says Walk; a coral X says target action; a gray slash says no-op.
 *  The dark under-stroke keeps each legible over both floor and sprites. */
export function drawCursorMark(ctx: CanvasRenderingContext2D, mark: CursorMark, now: number): void {
  const p = (now - mark.born) / CURSOR_MARK_DURATION;
  if (p < 0 || p >= 1) return;
  const alpha = p < 0.35 ? 1 : (1 - p) / 0.65;
  ctx.save();
  ctx.translate(mark.x, mark.y);
  ctx.globalAlpha = alpha;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.beginPath();
  if (mark.kind === "walk") {
    ctx.moveTo(0, -8);
    ctx.lineTo(8, 0);
    ctx.lineTo(0, 8);
    ctx.lineTo(-8, 0);
    ctx.closePath();
  } else if (mark.kind === "interact") {
    ctx.moveTo(-6, -6);
    ctx.lineTo(6, 6);
    ctx.moveTo(6, -6);
    ctx.lineTo(-6, 6);
  } else {
    ctx.moveTo(-6, 0);
    ctx.lineTo(6, 0);
  }
  ctx.strokeStyle = "#101010";
  ctx.lineWidth = 5;
  ctx.stroke();
  ctx.strokeStyle = mark.kind === "walk" ? "#FFDE59" : mark.kind === "interact" ? "#EF5A4B" : "#9A9A94";
  ctx.lineWidth = 2.5;
  ctx.stroke();
  ctx.restore();
}

/** One transient marker. `born` is a wall-clock timestamp (performance.now());
 *  markers are client-render-only and never cross a boundary. */
export type Marker =
  | { kind: "interact"; obj: WorldObject; born: number }
  | { kind: "ground"; x: number; y: number; born: number }
  | { kind: "invalid"; x: number; y: number; born: number };

/** Classify a pointer-down action into the marker that acknowledges it (R2).
 *  Pure — the game calls this synchronously before dispatching the command. */
export function markerForAction(action: PrimaryAction, clickX: number, clickY: number, born: number): Marker {
  switch (action.kind) {
    case "interact":
      return { kind: "interact", obj: action.obj, born };
    case "enter":
      // "Enter" through an open door is still an interact target — same
      // semantics as the old red X.
      return { kind: "interact", obj: action.door, born };
    case "openSack":
      // The sack's left-click (the item list) still outlines the sack — the
      // same feedback as an interact. The tile object carries itemId -1
      // (a menu opener, not a withdraw); the marker is keyed "sack:self"
      // (no id field — the same convention as the return portal).
      return { kind: "interact", obj: { kind: "sack", x: action.x, y: action.y, itemId: -1 }, born };
    case "walk":
      // Ground ripple at the RESOLVED destination (the tile the character
      // walks to), matching the old yellow-X placement — not the raw click
      // pixel (unwalkable clicks resolve to nearest revealed floor).
      return { kind: "ground", x: action.x, y: action.y, born };
    case "attack":
      // Flash the enemy's silhouette (the interact marker) — the ack for a
      // left-click attack (the arena; a game click never resolves to an
      // attack). `action.obj` is the enemy (WorldObject kind "enemy").
      return { kind: "interact", obj: action.obj, born };
    case "self":
    case "none":
      // Invalid: today only a self-click (clicks on walls/void resolve to a
      // walk). Machinery reserved for future genuinely-invalid cases.
      return { kind: "invalid", x: clickX, y: clickY, born };
  }
}

/** At most one marker per kind (R8): a new marker replaces the previous one
 *  of its own kind; different kinds coexist. Pure — the game calls this on
 *  pointer-down. */
export function pushMarker(list: Marker[], m: Marker): Marker[] {
  const out: Marker[] = [];
  for (const old of list) {
    if (old.kind !== m.kind) out.push(old);
  }
  out.push(m);
  return out;
}

export function markerDuration(m: Marker, cfg: MarkerConfig): number {
  switch (m.kind) {
    case "ground":
      return cfg.groundDuration;
    case "interact":
      return cfg.interactDuration;
    case "invalid":
      return cfg.invalidDuration;
  }
}

/** Normalized elapsed time in [0, 1]. */
export function markerProgress(m: Marker, now: number, cfg: MarkerConfig): number {
  const d = markerDuration(m, cfg);
  if (d <= 0) return 1;
  return Math.min(1, Math.max(0, (now - m.born) / d));
}

export function markerExpired(m: Marker, now: number, cfg: MarkerConfig): boolean {
  return now - m.born >= markerDuration(m, cfg);
}

/** Ground ripple radius: linear growth (R3). */
export function groundRx(p: number, cfg: MarkerConfig): number {
  return cfg.groundStartRx + (cfg.groundEndRx - cfg.groundStartRx) * p;
}

/** Ground alpha: (1 − p)² — fades fast, disappears cleanly (R3). */
export function groundAlpha(p: number): number {
  const q = 1 - p;
  return q * q;
}

/** Interact alpha: full for the first hold-fraction, then linear fade (R4). */
export function interactAlpha(p: number, cfg: MarkerConfig): number {
  if (p < cfg.interactHoldFrac) return 1;
  const rest = 1 - cfg.interactHoldFrac;
  return rest <= 0 ? 0 : 1 - (p - cfg.interactHoldFrac) / rest;
}

/** Invalid alpha: linear fade (R5). */
export function invalidAlpha(p: number): number {
  return 1 - p;
}

/** Invalid horizontal shake: sin(p·40)·(1−p)·px — decays to 0 at p = 1 (R5). */
export function invalidShakeX(p: number, cfg: MarkerConfig): number {
  return Math.sin(p * 40) * (1 - p) * cfg.invalidShakePx;
}

/** Stroke geometry + opacity for one interact outline (halo + colored pass). */
export interface OutlineStyle {
  /** Ring width outside the entity boundary (×u px). */
  dilation: number;
  /** Dark-halo extra width beyond the dilation (×u px). */
  haloWidth: number;
  /** Halo opacity multiplier (the halo is always haloColor at alpha·haloAlpha). */
  haloAlpha: number;
  /** Colored-pass opacity. */
  alpha: number;
}

/** Outline style for an active interact marker at `now`, or null once the
 *  flash has faded (alpha ≤ 0). */
export function clickOutlineStyle(m: Marker, now: number, cfg: MarkerConfig = MARKER_CONFIG): OutlineStyle | null {
  const alpha = interactAlpha(markerProgress(m, now, cfg), cfg);
  if (alpha <= 0) return null;
  return { dilation: cfg.interactDilation, haloWidth: cfg.haloWidthBonus, haloAlpha: cfg.haloAlpha, alpha };
}

// ---- DOM side: silhouette bake + outline draws ----------------------------

/** Source rect of a sprite image to outline (defaults: full frame). */
export interface SpriteDesc {
  image: HTMLImageElement;
  sx?: number;
  sy?: number;
  sw?: number;
  sh?: number;
}

const TAU = Math.PI * 2;
const SAMPLES = 16;
const silCache = new WeakMap<HTMLImageElement, Map<string, HTMLCanvasElement>>();

/**
 * silhouetteFrame keyed by an explicit string, for sources that are CANVASES —
 * the WeakMap above keys on an image element, and a canvas has no identity a
 * caller can hand it. The case that matters: the hero's sprint/Blink rim is
 * derived from the hero COMPOSITE, which is itself cached (hero.ts), so
 * re-dilating 16 copies into a fresh canvas per hero per frame is pure waste.
 * Bounded: each entry is a (dw+2d)×(dh+2d) canvas (~43 KB at the hero's 96×96
 * sheet), so the ceiling is ~1.5 MB.
 */
const silKeyCache = new Map<string, HTMLCanvasElement>();
const SIL_KEY_LIMIT = 32;

export function silhouetteForKey(
  src: CanvasImageSource,
  key: string,
  sw: number,
  sh: number,
  d: number,
  color: string,
): HTMLCanvasElement {
  const hit = silKeyCache.get(key);
  if (hit) return hit;
  const c = silhouetteFrame(src, 0, 0, sw, sh, d, color);
  silKeyCache.set(key, c);
  while (silKeyCache.size > SIL_KEY_LIMIT) {
    const oldest = silKeyCache.keys().next();
    if (oldest.done) break;
    silKeyCache.delete(oldest.value);
  }
  return c;
}

/**
 * Bake a flat-color silhouette of a sprite frame dilated by `d` source px,
 * cached per (image, source rect, d, color) so the halo and colored passes
 * each bake once and are then pure blits. 16 samples around a circle — not 8
 * compass offsets, which notch on the diagonals at the halo's larger
 * dilation.
 */
export function silhouetteFrame(
  img: CanvasImageSource,
  sx: number,
  sy: number,
  sw: number,
  sh: number,
  d: number,
  color: string,
): HTMLCanvasElement {
  const w = sw + d * 2;
  const h = sh + d * 2;
  const c = document.createElement("canvas");
  c.width = Math.ceil(w);
  c.height = Math.ceil(h);
  const g = c.getContext("2d")!;
  for (let i = 0; i < SAMPLES; i++) {
    const a = (i / SAMPLES) * TAU;
    g.drawImage(img, sx, sy, sw, sh, d + Math.cos(a) * d, d + Math.sin(a) * d, sw, sh);
  }
  g.globalCompositeOperation = "source-in";
  g.fillStyle = color;
  g.fillRect(0, 0, c.width, c.height);
  return c;
}

/**
 * Bake a flat-color silhouette of a sprite frame dilated by `d` source px,
 * cached per (image, source rect, d, color) so the halo and colored passes
 * each bake once and are then pure blits. 16 samples around a circle — not 8
 * compass offsets, which notch on the diagonals at the halo's larger
 * dilation.
 */
export function silhouette(
  img: HTMLImageElement,
  sx: number,
  sy: number,
  sw: number,
  sh: number,
  d: number,
  color: string,
): HTMLCanvasElement {
  let m = silCache.get(img);
  if (!m) {
    m = new Map();
    silCache.set(img, m);
  }
  const key = `${sx},${sy},${sw},${sh},${d},${color}`;
  const hit = m.get(key);
  if (hit) return hit;
  const c = silhouetteFrame(img, sx, sy, sw, sh, d, color);
  m.set(key, c);
  return c;
}

/**
 * Draw one outline pass (halo or colored) around a sprite. The sprite's own
 * dest rect is (destX, destY, destW, destH); the baked mask expands it by
 * `dilation` on every side. `dilation` is in dest css px — the bake converts
 * it to source px internally so the ring width is exact at any scale.
 */
export function outlineSprite(
  ctx: CanvasRenderingContext2D,
  d: SpriteDesc,
  destX: number,
  destY: number,
  destW: number,
  destH: number,
  dilation: number,
  color: string,
  alpha: number,
): void {
  const sx = d.sx ?? 0;
  const sy = d.sy ?? 0;
  const sw = d.sw ?? d.image.naturalWidth;
  const sh = d.sh ?? d.image.naturalHeight;
  const scale = destW / sw;
  const dSrc = dilation / scale;
  const sil = silhouette(d.image, sx, sy, sw, sh, dSrc, color);
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.drawImage(sil, destX - dilation, destY - dilation, destW + dilation * 2, destH + dilation * 2);
  ctx.restore();
}

/**
 * One outline pass (halo or colored) around a procedural shape: `trace`
 * builds the shape's boundary path in current space. The stroke is centred on
 * the boundary, so the entity's own fill (drawn afterwards) covers the inner
 * half — leaving a ring of ~`dilation` outside.
 */
export function outlinePath(
  ctx: CanvasRenderingContext2D,
  trace: (ctx: CanvasRenderingContext2D) => void,
  dilation: number,
  color: string,
  alpha: number,
): void {
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.strokeStyle = color;
  ctx.lineWidth = dilation * 2;
  ctx.lineJoin = "round";
  ctx.lineCap = "round";
  ctx.beginPath();
  trace(ctx);
  ctx.stroke();
  ctx.restore();
}
